import * as THREE from 'three/webgpu';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';
import type { SpectateCamera } from './SpectateCamera';
import { clamp, expDecay } from '../../shared/utils';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const _scratchDesired = new THREE.Vector3();
const _scratchVelocity = new THREE.Vector3();
const _scratchShake = new THREE.Vector3();
const _scratchDirShake = new THREE.Vector3();
// Slow-motion target during hit-stop. 0.2 ≈ 5x slowdown — hits land hard
// without freezing the action long enough to feel laggy.
const HIT_STOP_TIME_SCALE = 0.2;
// Default hit-stop duration if caller omits it. ~6 frames @60fps slow + 2-frame
// ramp-out = ~133ms total, matches the punchy feel described in the spec.
const HIT_STOP_DEFAULT_MS = 100;
// Ease-out window after the slow window expires; the time scale lerps from
// HIT_STOP_TIME_SCALE back to 1.0 over this many ms.
const HIT_STOP_RAMP_MS = 32;
// Exponential decay rate for orbit yaw/pitch tracking (s^-1). ~55ms time-
// constant — fast enough to feel near-instant under WoW-style right-click
// drag, slow enough that pixel jitter from a high-DPI mouse doesn't read as
// camera shimmer.
const ORBIT_DAMP_RATE = 18;
// Slightly softer zoom damping (~83ms) so wheel ticks ease in/out instead of
// snapping. Lower than ORBIT_DAMP_RATE because zoom velocity is naturally
// "weightier" — the camera has to slide through depth, not just rotate.
const ZOOM_DAMP_RATE = 12;

export class CameraController {
  private readonly camera: THREE.PerspectiveCamera;
  private readonly domElement: HTMLElement;
  private readonly qualityPreset: VisualQualityPreset;
  private readonly baseFov: number;

  private target = new THREE.Vector3(0, 20, 0);
  private desiredTarget = new THREE.Vector3(0, 20, 0);
  private currentPos = new THREE.Vector3(0, 40, 60);
  private lastDesiredTarget = new THREE.Vector3(0, 20, 0);
  private targetVelocity = new THREE.Vector3();
  private orbitAngle = 0;
  private orbitPitch = -0.4;
  private distance = 35;
  // Desired-state mirrors of orbitAngle/orbitPitch/distance. Mouse and wheel
  // input write to these immediately; update() exp-lerps the live fields
  // toward them each frame. Without this split the camera snapped one-to-one
  // to mouse motion, which read as rubbery against the slower follow lerp.
  private desiredOrbitAngle = 0;
  private desiredOrbitPitch = -0.4;
  private desiredDistance = 35;
  private smoothSpeed = 4;
  private shakeIntensity = 0;
  private fovKick = 0;
  private shakeTime = 0;
  private spectateCamera: SpectateCamera | null = null;
  /**
   * Persistent unit vector pointing along the requested shake direction.
   * Multiplied by `dirShakeIntensity` each frame to produce the camera offset.
   */
  private dirShakeAxis = new THREE.Vector3();
  /** Decaying scalar magnitude of the directional shake. */
  private dirShakeIntensity = 0;
  /**
   * Active slow-motion window (ms remaining). While > 0, getTimeScale()
   * returns HIT_STOP_TIME_SCALE; once this reaches 0 the ramp-out begins.
   */
  private hitStopRemainingMs = 0;
  /**
   * Ramp-out window (ms remaining) following the slow window. While > 0,
   * getTimeScale() lerps from HIT_STOP_TIME_SCALE back toward 1.0.
   */
  private hitStopRampRemainingMs = 0;
  /**
   * Mouse sensitivity multiplier applied to {@link applyMouseDelta}. 1.0 is
   * the baseline; higher values rotate faster per pixel, lower values slower.
   * Owned by the user-facing MouseSettingsPanel and pushed via
   * {@link setMouseSensitivity}.
   */
  private sensitivity = 1.0;
  /** When true, {@link applyMouseDelta} negates dy before pitch integration. */
  private invertPitch = false;

  constructor(
    camera: THREE.PerspectiveCamera,
    domElement: HTMLElement,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
  ) {
    this.camera = camera;
    this.domElement = domElement;
    this.qualityPreset = qualityPreset;
    this.baseFov = camera.fov;
    this.currentPos.copy(camera.position);
  }

  setFollowTarget(pos: Vec3): void {
    this.desiredTarget.set(pos.x, pos.y, pos.z);
  }

  attachSpectateCamera(spectate: SpectateCamera): void {
    this.spectateCamera = spectate;
  }

  getMode(): 'follow' | 'spectate' | 'killcam' {
    return this.spectateCamera?.getMode() ?? 'follow';
  }

  update(deltaMs: number): void {
    const dt = Math.max(deltaMs / 1000, 1 / 240);

    // Override the follow target when spectating or in kill-cam. The lerp
    // chain below remains identical, so existing smoothing/shake/FOV logic
    // is preserved across modes.
    if (this.spectateCamera) {
      this.spectateCamera.update(deltaMs);
      const override = this.spectateCamera.getDesiredTarget();
      if (override) this.desiredTarget.set(override.x, override.y, override.z);
    }

    _scratchVelocity.set(
      (this.desiredTarget.x - this.lastDesiredTarget.x) / dt,
      (this.desiredTarget.y - this.lastDesiredTarget.y) / dt,
      (this.desiredTarget.z - this.lastDesiredTarget.z) / dt,
    );
    this.targetVelocity.lerp(_scratchVelocity, 0.12);
    this.lastDesiredTarget.copy(this.desiredTarget);

    const targetLerp = 1 - Math.exp(-this.smoothSpeed * dt);
    this.target.lerp(this.desiredTarget, targetLerp);

    const orbitLerp = expDecay(ORBIT_DAMP_RATE, dt);
    this.orbitAngle = THREE.MathUtils.lerp(this.orbitAngle, this.desiredOrbitAngle, orbitLerp);
    this.orbitPitch = THREE.MathUtils.lerp(this.orbitPitch, this.desiredOrbitPitch, orbitLerp);
    this.distance = THREE.MathUtils.lerp(this.distance, this.desiredDistance, expDecay(ZOOM_DAMP_RATE, dt));

    const cosPitch = Math.cos(this.orbitPitch);
    _scratchDesired.set(
      this.target.x + this.distance * Math.cos(this.orbitAngle) * cosPitch,
      this.target.y + this.distance * Math.abs(Math.sin(this.orbitPitch)),
      this.target.z + this.distance * Math.sin(this.orbitAngle) * cosPitch,
    );

    const lerpFactor = 1 - Math.exp(-this.smoothSpeed * dt);
    this.currentPos.lerp(_scratchDesired, lerpFactor);

    this.shakeTime += dt;
    this.shakeIntensity = Math.max(0, this.shakeIntensity - dt * 2.2);
    this.fovKick = Math.max(0, this.fovKick - dt * 16);
    // Same decay slope as `shakeIntensity` so a directional shake settles in
    // the same ~0.5s envelope as the existing scalar shake.
    this.dirShakeIntensity = Math.max(0, this.dirShakeIntensity - dt * 2.2);
    // Hit-stop timers tick on wall-clock dt — they must NOT slow down their
    // own clock (otherwise the slow-mo window would never end). When a tick
    // straddles the slow→ramp boundary we apply the leftover dt to the ramp
    // in the same call so a long frame can't get "stuck" at 0.2.
    if (this.hitStopRemainingMs > 0) {
      const consumed = Math.min(deltaMs, this.hitStopRemainingMs);
      this.hitStopRemainingMs -= consumed;
      if (this.hitStopRemainingMs === 0) {
        const overshoot = deltaMs - consumed;
        this.hitStopRampRemainingMs = Math.max(0, HIT_STOP_RAMP_MS - overshoot);
      }
    } else if (this.hitStopRampRemainingMs > 0) {
      this.hitStopRampRemainingMs = Math.max(0, this.hitStopRampRemainingMs - deltaMs);
    }

    _scratchShake.set(
      Math.sin(this.shakeTime * 38.0) * 0.3 + Math.sin(this.shakeTime * 91.0) * 0.15,
      Math.sin(this.shakeTime * 43.0 + 1.7) * 0.24 + Math.cos(this.shakeTime * 77.0) * 0.1,
      Math.cos(this.shakeTime * 34.0 + 0.8) * 0.22,
    ).multiplyScalar(this.shakeIntensity);
    // Layer the directional offset on top of the procedural shake. The
    // axis is unit-length (set in addDirectionalShake), so the magnitude
    // is solely controlled by `dirShakeIntensity`.
    if (this.dirShakeIntensity > 0) {
      _scratchDirShake.copy(this.dirShakeAxis).multiplyScalar(this.dirShakeIntensity);
      _scratchShake.add(_scratchDirShake);
    }

    const motionBoost = this.qualityPreset.features.dynamicCamera
      ? Math.min(3.6, this.targetVelocity.length() * 0.035)
      : 0;
    const desiredFov = this.baseFov + motionBoost + this.fovKick;
    if (Math.abs(this.camera.fov - desiredFov) > 0.01) {
      this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, desiredFov, 0.12);
      this.camera.updateProjectionMatrix();
    }

    this.camera.position.copy(this.currentPos).add(_scratchShake);
    this.camera.lookAt(this.target.x, this.target.y, this.target.z);
    this.domElement.dataset.cameraOrbitAngle = this.orbitAngle.toFixed(3);
    this.domElement.dataset.cameraOrbitPitch = this.orbitPitch.toFixed(3);
    this.domElement.dataset.cameraDistance = this.distance.toFixed(2);
  }

  applyMouseDelta(dx: number, dy: number): void {
    const dyApplied = this.invertPitch ? -dy : dy;
    this.desiredOrbitAngle += dx * 0.005 * this.sensitivity;
    this.desiredOrbitPitch = clamp(
      this.desiredOrbitPitch + dyApplied * 0.005 * this.sensitivity,
      -1.3,
      0.1,
    );
  }

  /**
   * Update the mouse sensitivity multiplier and pitch inversion flag.
   * Called by the MouseSettingsPanel via the GameClient wiring whenever
   * the user moves a slider or toggles the invert checkbox.
   */
  setMouseSensitivity(sensitivity: number, invertPitch: boolean): void {
    this.sensitivity = sensitivity;
    this.invertPitch = invertPitch;
  }

  getOrbitAngle(): number {
    return this.orbitAngle;
  }

  applyZoom(delta: number): void {
    this.desiredDistance = clamp(this.desiredDistance + delta * 0.5, 10, 80);
  }

  addShake(intensity: number): void {
    if (!this.qualityPreset.features.dynamicCamera) return;
    this.shakeIntensity = Math.min(1.2, this.shakeIntensity + intensity);
  }

  addFovKick(amount: number): void {
    if (!this.qualityPreset.features.dynamicCamera) return;
    this.fovKick = Math.min(4.5, this.fovKick + amount);
  }

  /**
   * Nudge the camera along `direction` for a few frames before settling.
   * The vector is normalized internally; magnitude is supplied via
   * `intensity`. The decay slope mirrors {@link addShake} so directional
   * and scalar shakes feel like the same family of impulse.
   *
   * Stacking: a fresh call replaces the axis but ADDS to the intensity
   * (capped at 1.2, matching `addShake`). This way rapid hits in slightly
   * different directions still build up rather than cancel each other.
   *
   * No-op on quality presets without `dynamicCamera`, like {@link addShake}.
   *
   * Accepts any `{x,y,z}` shape (THREE.Vector3 or plain Vec3) — callers
   * shouldn't have to import three/webgpu just to pass a network payload's
   * knockback vector.
   */
  addDirectionalShake(direction: Vec3, intensity: number): void {
    if (!this.qualityPreset.features.dynamicCamera) return;
    if (intensity <= 0) return;
    const x = direction.x;
    const y = direction.y;
    const z = direction.z;
    const lenSq = x * x + y * y + z * z;
    if (lenSq < 1e-6) return;
    const inv = 1 / Math.sqrt(lenSq);
    this.dirShakeAxis.set(x * inv, y * inv, z * inv);
    this.dirShakeIntensity = Math.min(1.2, this.dirShakeIntensity + intensity);
  }

  /**
   * Apply a brief client-clock slow-motion ("hit-stop") for the supplied
   * duration. Subsequent calls to {@link getTimeScale} return
   * {@link HIT_STOP_TIME_SCALE} for the slow window, then ramp back to 1.0
   * over {@link HIT_STOP_RAMP_MS}.
   *
   * Does NOT touch the server clock — only this controller's time-scale
   * reading. Callers (GameClient) are responsible for scaling renderer
   * deltaMs by `getTimeScale()` to make the slow-mo visible.
   *
   * Stacking takes the longer of (current remaining, new duration) — a
   * shorter follow-up crit won't shorten the in-flight slow window, but a
   * longer one will extend it.
   */
  triggerHitStop(durationMs: number = HIT_STOP_DEFAULT_MS): void {
    if (!this.qualityPreset.features.dynamicCamera) return;
    if (durationMs <= 0) return;
    this.hitStopRemainingMs = Math.max(this.hitStopRemainingMs, durationMs);
  }

  /**
   * Frame-time multiplier for the rest of the client render loop. Returns
   * 1.0 outside the hit-stop window. During the slow window returns
   * {@link HIT_STOP_TIME_SCALE}. During the ramp window lerps linearly
   * from HIT_STOP_TIME_SCALE back to 1.0.
   *
   * Always returns >= HIT_STOP_TIME_SCALE so animations never freeze
   * fully — they just slow.
   */
  getTimeScale(): number {
    if (this.hitStopRemainingMs > 0) return HIT_STOP_TIME_SCALE;
    if (this.hitStopRampRemainingMs > 0) {
      const t = this.hitStopRampRemainingMs / HIT_STOP_RAMP_MS;
      return THREE.MathUtils.lerp(1.0, HIT_STOP_TIME_SCALE, t);
    }
    return 1.0;
  }

  dispose(): void {}
}
